import Vue from 'vue'
import VueRouter from 'vue-router'

import {
    setLocaleFromRoute,
    authorizedUser,
    adminUser,
    isReportService
} from '@/router/RouterGuards';

Vue.use(VueRouter);

// Definition of routes with dynamic component loading.
const routes = [
    {
        name: 'auth',
        path: '/:locale/auth',
        component: () => import(/* webpackChunkName: "auth" */ '@/views/Auth'),
        beforeEnter: (to, from, next) => {
            const localeRoute = setLocaleFromRoute(to);
            if (localeRoute !== true) return next(localeRoute);

            return next();
        },
        children: [
            {
                name: 'login',
                meta: { title: 'Login' },
                path: '/:locale/auth/login',
                component: () => import(/* webpackChunkName: "login" */ '@/components/auth/LoginForm')
            },
            {
                name: 'new-password',
                meta: { title: 'New password' },
                path: '/:locale/auth/new-password',
                component: () => import(/* webpackChunkName: "new-password" */ '@/components/auth/NewPasswordForm'),
                beforeEnter: (to, from, next) => {
                    if (
                        (from.name === 'login' || from.name === 'new-password') &&
                        to.params.user !== undefined
                    ) return next();
                    
                    return next({ name: 'login' });
                }
            },
            {
                name: 'reset-password',
                meta: { title: 'Recovery password' },
                path: '/:locale/auth/reset-password',
                component: () => import(/* webpackChunkName: "reset-password" */ '@/components/auth/ResetPasswordForm')
            },
            {
                name: 'confirm-account',
                meta: { title: 'Confirm your account' },
                path: '/:locale/auth/confirm/:code',
                component: () => import(/* webpackChunkName: "confirm-account" */ '@/components/auth/ConfirmAccountForm')
            }
        ]
    },
    {
        path: '/:locale/dashboard',
        component: () => import(/* webpackChunkName: "main" */ '@/views/Main'),
        beforeEnter: async (to, from, next) => {
            const localeRoute = setLocaleFromRoute(to);
            if (localeRoute !== true) return next(localeRoute);
            return await authorizedUser() ? next() : next({ name: 'login' });
        },
        children: [
            {
                path: '',
                name: 'dashboard',
                meta: { title: 'Dashboard' },
                component: () => import(/* webpackChunkName: "dashboard" */ '@/views/Dashboard'),
            },
            {
                path: 'profile',
                name: 'profile',
                meta: { title: 'My profile' },
                component: () => import(/* webpackChunkName: "profile" */ '@/views/Profile')
            },
            {
                path: 'customers',
                name: 'customers',
                meta: { title: 'Customers managment' },
                component: () => import(/* webpackChunkName: "customers" */ '@/views/Customers'),
                beforeEnter: async (to, from, next) => {
                    const localeRoute = setLocaleFromRoute(to);
                    if (localeRoute !== true) return next(localeRoute);

                    const isAdmin = await adminUser();
                    if (!isAdmin) return next({ name: 'login' });
                        
                    return next();
                },
            },
        ]
    },
    {
        name: 'report',
        path: '/:locale/report',
        component: () => import(/* webpackChunkName: "report" */ '@/views/Report'),
        beforeEnter: async (to, from, next) => {
            return isReportService(to.query) ? next() : next({ name: 'login' });
        }
    },
    {
        name: 'ghl',
        path: '/:locale/ghl',
        component: () => import(/* webpackChunkName: "ghl" */ '@/views/EmbebbedDashboard'),
        beforeEnter: async (to, from, next) => {
            const { ghl_key, account_id } = to.query;
            const { locale } = to.params;

            // Require either account_id or ghl_key
            if (!account_id && !ghl_key) {
                return next({ name: 'not-found', query: { ghl_key, account_id }, params: { locale } });
            }
            return next();
        },
    },
    {
        name: 'not-found',
        path: '/:locale/not-found',
        meta: { title: 'Not found' },
        component: () => import(/* webpackChunkName: "not-found" */ '@/views/NotFound'),
        beforeEnter: (to, from, next) => {
            const localeRoute = setLocaleFromRoute(to);
            if (localeRoute !== true) return next(localeRoute);

            return next();
        },
    },
    {
        path: '*',
        redirect() {
            return { 
                name: 'dashboard', 
                params: { 
                    locale: process.env_APP_I18N_LOCALE 
                } 
            };
        }
    }
]

// Initialize router to export it after.
const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
});

// Set a dynamic web title on change view.
const baseTitle = 'DearDoc Reporting App';
const titleSeparator = ' | ';
router.beforeEach((to, _, next) => {
    window.gtag('event', 'screen_view', { 'screen_name': to.meta.title });
    document.title = to.meta.title ? 
        to.meta.title + titleSeparator + baseTitle : baseTitle;
    next();
});

export default router;
